import PopUpBase from '@components/PopUpBase';
import BaseEnhancedTabs, { TabConfig } from '@components/tabs/BaseEnhancedTabs';
import { pushSuccess, pushWarning } from '@components/toast';
import { cameraOverviewHttpController, taskHttpController } from '@controllers/index';
import { IGetConsolidateCameraMonitoring } from '@EcamModel/controllers/ICameraOverviewHttpController';
import { CameraTask, SpecificTaskTypes } from '@EcamModel/model/CameraTask';
import { IPopUp } from '@hooks/usePopUp';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, Typography } from '@mui/material';
import { DetailsInformation } from '@pages/cameras/details/components/CameraInformation';
import { BaseSelect } from '@pages/cameras/list';
import { CustomTabPanel } from '@pages/overview/tabs/useTabsData';
import { ellipsisStyles } from '@pages/task-management';
import {
    CameraSubTasks,
    mappingPriorityTask,
    priorityStyles,
    SubTaskStatus,
    taskTypeToTabMap,
} from '@pages/task-management/config';
import color from '@theme/Colors';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { endOfStartDate, formatUKDate, startOfThreeDaysAgo } from 'src/helpers';
import TabCheckBatteryHealth from '../tab/TabCheckBatteryHealth';
import TabCheckDeviation from '../tab/TabCheckDeviation';
import TabCheckSDCard from '../tab/TabCheckSDCard';

type Props = IPopUp & {
    taskItem: CameraTask;
    onSetTaskItem: (task: CameraTask) => void;
};

export enum ViewTaskTab {
    BaseInfo,
    Deviation,
    Battery,
    SdCard,
}

export default function PopupViewCompleteTask(props: Props) {
    const { open, taskItem, onSetTaskItem, onClose } = props;

    const { id: cameraId } = useParams();

    const [valueTabs, setValueTabs] = useState<ViewTaskTab>(ViewTaskTab.BaseInfo);
    const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
    const queryClient = useQueryClient();

    const parsedSubTasks: CameraSubTasks[] = JSON.parse(taskItem.SubTask ?? '[]');
    const formattedTaskTags = parsedSubTasks?.map(
        (task, index, array) => `${task.Name} (${task.Status})${index === array.length - 1 ? '.' : ','}`
    );

    let parsedTaskTypes: BaseSelect[] = [];

    try {
        const taskTypeString = taskItem?.TaskType ?? '';
        if (taskTypeString) {
            parsedTaskTypes = JSON.parse(taskTypeString);
        }
    } catch (error) {
        console.error('Error parsing TaskType JSON:', error);
        parsedTaskTypes = [];
    }

    const taskTypeString = parsedTaskTypes?.map((taskType) => taskType);

    const filteredTabs: TabConfig[] = [
        { label: 'Base information', index: ViewTaskTab.BaseInfo },
        ...parsedSubTasks
            .map((task) => taskTypeToTabMap[task.Name as SpecificTaskTypes])
            .filter((tab): tab is TabConfig => !!tab)
            .filter((tab, index, self) => self.findIndex((t) => t?.index === tab?.index) === index)
            .sort((a, b) => a.index - b.index),
    ];

    const currentTabIndex = filteredTabs[valueTabs]?.index;

    const detailTask: DetailsInformation[] = [
        {
            id: 1,
            label: 'Task:',
            value: <Typography variant="body1">{taskItem.TaskName ?? ''}</Typography>,
        },
        {
            id: 2,
            label: 'Sub task:',
            value: (
                <Typography
                    sx={{
                        ...ellipsisStyles,
                        whiteSpace: 'pre-line',
                    }}
                    component={'span'}
                >
                    {formattedTaskTags.length > 0
                        ? formattedTaskTags.join(', ').replace(/, /g, '\n')
                        : taskTypeString.join(', ')}
                </Typography>
            ),
        },
        {
            id: 3,
            label: 'Priority:',
            value: (
                <Typography
                    padding={'4px 8px'}
                    variant="body2"
                    borderRadius={'5px'}
                    width={'max-content'}
                    sx={{
                        cursor: 'default',
                        color: priorityStyles[taskItem?.TaskPriority ?? '']?.color,
                        bgcolor: priorityStyles[taskItem?.TaskPriority ?? '']?.backgroundColor,
                        fontSize: 12,
                    }}
                >
                    {mappingPriorityTask[taskItem.TaskPriority ?? '']}
                </Typography>
            ),
        },
        {
            id: 4,
            label: 'Assignee:',
            value: <Typography variant="body1">{taskItem.Assignee ?? ''}</Typography>,
        },
        {
            id: 5,
            label: 'Problem:',
            value: (
                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {taskItem.Problem ?? ''}
                </Typography>
            ),
        },
        {
            id: 6,
            label: 'Solution:',
            value: (
                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                    {taskItem.Solution ?? ''}
                </Typography>
            ),
        },
        // {
        //     id: 4,
        //     label: 'Attending date',
        //     value: <Typography variant="body1">{formatUKDate(taskItem.AttendingDate!)}</Typography>,
        // },
        ...(!_.isNil(taskItem.CompleteDate)
            ? [
                  {
                      id: 7,
                      label: 'Complete date:',
                      value: <Typography variant="body1">{formatUKDate(taskItem.CompleteDate!)}</Typography>,
                  },
              ]
            : []),
    ];

    const handleUpdateTaskStatus = (specificSubTask: SpecificTaskTypes) => {
        const updatedSubTasks: CameraSubTasks[] = taskItem.SubTask ? JSON.parse(taskItem.SubTask) : [];

        const newTaskTypes = updatedSubTasks.map((task) =>
            task.Name === specificSubTask
                ? { ...task, Status: SubTaskStatus.Done, Name: specificSubTask, CompletedAt: moment().toDate() }
                : task
        );

        handleSaveTask(JSON.stringify(newTaskTypes));
    };

    const handleSaveTask = (subTask: string) => {
        if (taskItem.SubTask === subTask) {
            pushWarning('This sub task has been completed previously');
            return;
        }
        setIsLoadingSave(true);

        taskHttpController
            .upsert({
                Id: taskItem.Id,
                SubTask: subTask,
                CameraId: taskItem.CameraId,
            } as CameraTask)
            .then((res) => {
                queryClient.invalidateQueries(['listTask']);
                queryClient.invalidateQueries(['trailAuditEvent']);
                onSetTaskItem({ ...res, SubTask: res.SubTask });
                pushSuccess('Update successfully');
            })
            .finally(() => {
                setIsLoadingSave(false);
            });
    };

    const getInformationBattery = async () => {
        const hasBatteryOrSdCard = filteredTabs.some(
            (t) => t.index === ViewTaskTab.Battery || t.index === ViewTaskTab.SdCard
        );

        if (!hasBatteryOrSdCard) {
            return;
        }

        const _filter: IGetConsolidateCameraMonitoring = {
            cameraId: taskItem.CameraId,
            startDate: startOfThreeDaysAgo,
            endDate: endOfStartDate,
        };

        const response = await cameraOverviewHttpController.getConsolidateCameraMonitoring(_filter);
        return response;
    };

    const {
        data: cameraConfigMonitoring,
        error: errorCameraConfigMonitoring,
        isLoading: loadingCameraConfigMonitoring,
    } = useQuery(['cameraConfigMonitoring', taskItem.Id, taskItem.CameraId], getInformationBattery, {
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!taskItem.Id,
    });

    const isTaskDone = () => {
        return parsedSubTasks.some(
            (task) =>
                taskTypeToTabMap[task.Name as SpecificTaskTypes]?.index === currentTabIndex &&
                task.Status === SubTaskStatus.Done
        );
    };

    const handleCancel = () => {
        onClose?.();
        onSetTaskItem({} as CameraTask);
    };

    const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
        setValueTabs(newValue);
    };

    useEffect(() => {
        if (!open) return;
        setValueTabs(ViewTaskTab.BaseInfo);
    }, [open]);

    return (
        <PopUpBase
            title={
                <Typography
                    variant="h4"
                    sx={{
                        ...ellipsisStyles,
                    }}
                >
                    View task: {taskItem.TaskName ?? ''}
                </Typography>
            }
            fixOverflow
            dialogProps={{
                fullWidth: true,
                sx: {
                    '& .MuiDialog-paper': {
                        borderRadius: '10px',
                        minWidth: 850,
                    },
                    zIndex: (theme) => theme.zIndex.modal,
                },
            }}
            onClickClose={handleCancel}
            open={open}
            onClose={onClose}
            hideConfirm
            minWidthButton={130}
            desc={
                <>
                    <BaseEnhancedTabs
                        tabs={filteredTabs}
                        value={valueTabs}
                        onChange={handleChangeTabs}
                        minWidth={'10%'}
                    />

                    {filteredTabs.map((tab, index) => (
                        <CustomTabPanel value={valueTabs} index={index} key={index}>
                            <Box bgcolor={color.white}>
                                {tab.index == ViewTaskTab.BaseInfo && (
                                    <Stack
                                        direction={'column'}
                                        spacing={3}
                                        bgcolor={color.grey100}
                                        borderRadius={'8px'}
                                        p={2}
                                    >
                                        <Stack direction={'column'} spacing={1}>
                                            <Typography variant="h6" color={color.success}>
                                                Information
                                            </Typography>
                                            <Stack
                                                width={'100%'}
                                                padding={2}
                                                bgcolor={color.white}
                                                spacing={2}
                                                borderRadius={'4px'}
                                                sx={{
                                                    maxHeight: '35vh',
                                                    overflowY: 'auto',
                                                    '::-webkit-scrollbar': {
                                                        width: '4px',
                                                    },
                                                }}
                                            >
                                                {detailTask.map((item) => (
                                                    <Stack
                                                        key={item.id}
                                                        direction={'row'}
                                                        width={'100%'}
                                                        justifyContent={'space-between'}
                                                    >
                                                        <Stack width={'40%'}>
                                                            <Typography variant="h6">{item.label}</Typography>
                                                        </Stack>
                                                        <Stack width={'60%'}>
                                                            <Typography variant="body1">{item.value}</Typography>
                                                        </Stack>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </Stack>

                                        <Stack direction={'column'} spacing={1}>
                                            <Typography variant="h6" color={color.success}>
                                                Note
                                            </Typography>
                                            <Stack
                                                width={'100%'}
                                                padding={2}
                                                bgcolor={color.white}
                                                spacing={2}
                                                borderRadius={'4px'}
                                                sx={{
                                                    maxHeight: '15vh',
                                                    overflowY: 'auto',
                                                    '::-webkit-scrollbar': {
                                                        width: '4px',
                                                    },
                                                }}
                                            >
                                                <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                                                    {taskItem.Note ?? ''}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                )}

                                {tab.index === ViewTaskTab.Deviation && (
                                    <TabCheckDeviation cameraId={taskItem.CameraId} />
                                )}
                                {tab.index === ViewTaskTab.Battery && (
                                    <TabCheckBatteryHealth
                                        taskItem={taskItem}
                                        cameraConfigMonitoring={cameraConfigMonitoring || []}
                                        loadingCameraConfigMonitoring={loadingCameraConfigMonitoring}
                                    />
                                )}
                                {tab.index === ViewTaskTab.SdCard && (
                                    <TabCheckSDCard
                                        taskItem={taskItem}
                                        cameraConfigMonitoring={cameraConfigMonitoring || []}
                                        loadingCameraConfigMonitoring={loadingCameraConfigMonitoring}
                                    />
                                )}
                            </Box>
                        </CustomTabPanel>
                    ))}
                </>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        variant="cancel"
                        onClick={() => {
                            handleCancel();
                        }}
                    >
                        Close
                    </Button>
                    {valueTabs !== ViewTaskTab.BaseInfo && (
                        <Stack direction={'row'} spacing={2}>
                            {!cameraId && (
                                <Button
                                    variant="outlinedGreen"
                                    onClick={() => {
                                        window.open(`/cameras/${taskItem.CameraId}`, '_blank');
                                    }}
                                >
                                    View camera detail
                                </Button>
                            )}
                            <LoadingButton
                                sx={{
                                    minWidth: 130,
                                }}
                                loading={isLoadingSave}
                                loadingPosition="center"
                                variant="contained"
                                disabled={isLoadingSave || isTaskDone()}
                                onClick={() => {
                                    handleUpdateTaskStatus(
                                        currentTabIndex === ViewTaskTab.Deviation
                                            ? SpecificTaskTypes.AngleDeviation
                                            : currentTabIndex === ViewTaskTab.Battery
                                            ? SpecificTaskTypes.BatteryHealth
                                            : SpecificTaskTypes.SDCard
                                    );
                                }}
                            >
                                {`${isTaskDone() ? 'Checked (Done)' : 'Checked'}`}
                            </LoadingButton>
                        </Stack>
                    )}
                </Stack>
            }
        />
    );
}
